import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Heart, Home, Download, Repeat, Sparkles } from 'lucide-react';

const ThankYouPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [animateElements, setAnimateElements] = useState(false);

  // Get donation details from URL parameters
  const donorName = searchParams.get('name') || '';
  const amount = searchParams.get('amount') || '51';
  const seva = searchParams.get('seva') || 'General Seva';
  const isRecurring = searchParams.get('recurring') === 'true';

  useEffect(() => {
    // Trigger animations after component mounts
    const timer = setTimeout(() => {
      setAnimateElements(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const handleReturnHome = () => {
    navigate('/');
  };

  const handleMakeAnotherDonation = () => {
    navigate('/', { state: { scrollToSeva: true } });
  };

  return (
    <div className="thank-you-page">
      <div 
        className="thank-you-background"
        
      >
        <div className="thank-you-overlay">
          <div className="container">
            {/* Success Icon - positioned above both cards */}
            <div className={`success-icon-container ${animateElements ? 'animate' : ''}`}>
              <div className="success-icon-bg">
                <CheckCircle className="success-icon" />
              </div>
              <div className="sparkles">
                <Sparkles className="sparkle sparkle-1" />
                <Sparkles className="sparkle sparkle-2" />
                <Sparkles className="sparkle sparkle-3" />
              </div>
            </div>

            <div className={`thank-you-content ${animateElements ? 'animate' : ''}`}>
              {/* Main Thank You Card */}
              <Card className="thank-you-card">
                <CardContent className="thank-you-card-content">
                  <div className="gratitude-section">
                    <h1 className="thank-you-title">
                      Hare Krishna{donorName ? `, ${donorName}` : ` ${donorName}`}!
                    </h1>
                    <p className="thank-you-subtitle">
                      Your donation has been received with deep gratitude
                    </p>
                  </div>

                  <div className="donation-details">
                    <div className="detail-row">
                      <span className="detail-label">Seva:</span>
                      <span className="detail-value">{seva}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Amount:</span>
                      <Badge variant="secondary" className="amount-badge">
                        ${amount} {isRecurring ? 'monthly' : 'one-time'}
                      </Badge>
                    </div>
                    {isRecurring && (
                      <div className="detail-row">
                        <Repeat className="w-4 h-4 text-saffron" />
                        <span className="recurring-info">Monthly subscription active</span>
                      </div>
                    )}
                  </div>

                  <div className="spiritual-message">
                    <div className="quote-section">
                      <p className="spiritual-quote">
                        "The Supreme Lord is equally disposed to all living entities. 
                        There is no one dear to Him, and no one is His enemy. 
                        But whoever renders service unto Him in devotion, 
                        such a person is very dear to Him."
                      </p>
                      <cite className="quote-source">Bhagavad Gita 9.29</cite>
                    </div>
                  </div>

                  {isRecurring && (
                    <div className="prasadam-info">
                      <Heart className="w-5 h-5 text-saffron" />
                      <p>Please collect Mahaprasad from the gift shop whenever you visit the temple.</p>
                    </div>
                  )}

                  <div className="action-buttons">
                    <Button
                      onClick={handleReturnHome}
                      className="primary-action-btn"
                      size="lg"
                    >
                      <Home className="w-4 h-4 mr-2" />
                      Return to Home
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleMakeAnotherDonation}
                      className="secondary-action-btn"
                      size="lg"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Make Another Donation
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Information Card */}
              <div className="additional-info">
                <Card className="info-card">
                  <CardContent className="info-content">
                    <h3 className="info-title">What happens next?</h3>
                    <ul className="info-list">
                      <li>📧 You'll receive a confirmation email shortly</li>
                      <li>🙏 Your donation directly supports book distribution and preaching activities</li>
                      {isRecurring && <li>🍽️ Please collect Mahaprasad from the gift shop whenever you visit the temple.</li>}
                      <li>📚 Learn more about our activities at <a href="https://iskconboston.org" target="_blank" rel="noopener noreferrer">ISKCON Boston</a></li>
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
